/**
 * Prévia da IA de uma conversa, feita para ler de relance:
 *
 * 1. Uma linha de estado (quem espera, a urgência) com a única cor do cartão.
 * 2. A manchete: a situação numa frase, em letra maior.
 * 3. Quem precisa agir, agrupado por Equipe, Cliente e Outro.
 * 4. O que aconteceu, no máximo 4 pontos.
 * 5. O próximo passo em destaque sutil.
 * 6. As decisões, recolhidas.
 *
 * `ConteudoDaPrevia` é o mesmo no topo da conversa e no balão da lista.
 * `CartaoDaPrevia` é a faixa do topo da conversa: uma linha com a manchete
 * que abre o conteúdo ao clicar (a escolha fica na sessão).
 *
 * A prévia vem das rodadas em lote ("Prévia das 13:00"). Refazer na hora é
 * escolha da pessoa e gasta 1 dos pedidos do dia ao Gemini: o botão diz isso
 * e quantos sobram, e só aparece quando chegou mensagem depois da prévia.
 */
import React, { useId, useState } from "react";
import { FiArrowRight, FiChevronDown, FiRefreshCw } from "react-icons/fi";
import styled from "styled-components";

import { MiniSpinner } from "../../../components/LoadingMarcenaria";
import { estadoDaPrevia, quemPrecisaAgir, textoDaPrevia } from "../apresentacao";
import { avisoDoPedido } from "../gemini";
import IconeDaIA from "../IconeDaIA";
import { BotaoCompacto, BotaoDeTexto, Osso } from "./estilos";

const CHAVE_RECOLHIDA = "central-projetos:previa-recolhida";
/** Mais que isto vira lista de compras: a prévia mostra os 4 primeiros pontos. */
const MAXIMO_DE_PONTOS = 4;
const tom = (nome, parte) => `var(--${nome || "neutral"}-${parte})`;

/** Recolhida por padrão: a manchete basta para decidir se vale abrir. */
function lerRecolhida() {
  try {
    return window.sessionStorage.getItem(CHAVE_RECOLHIDA) !== "0";
  } catch {
    // Sem sessionStorage (aba privada): fica no padrão.
    return true;
  }
}

function gravarRecolhida(valor) {
  try {
    window.sessionStorage.setItem(CHAVE_RECOLHIDA, valor ? "1" : "0");
  } catch {
    // Sem sessionStorage (aba privada): a escolha vale só enquanto a tela está aberta.
  }
}

/* ---------- Conteúdo ---------- */

const Leitura = styled.div`
  display: grid;
  gap: 18px;
  max-width: 64ch;
  color: var(--ink-2);
  font-size: 14px;
  line-height: 1.55;
`;

const Topo = styled.div`
  display: grid;
  gap: 6px;
`;

const Estado = styled.p`
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-2);

  &::before {
    content: "";
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $tom }) => tom($tom, "solid")};
  }
`;

const Manchete = styled.p`
  margin: 0;
  font-size: 16.5px;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: -0.005em;
  color: var(--ink);
  text-wrap: pretty;
`;

const Bloco = styled.section`
  display: grid;
  gap: 8px;
  min-width: 0;

  > h4 {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-3);
  }
`;

const Pontos = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 6px;

  li {
    position: relative;
    padding-left: 15px;
    text-wrap: pretty;

    &::before {
      content: "";
      position: absolute;
      left: 3px;
      top: 0.68em;
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: var(--ink-4);
    }
  }
`;

/* Quem precisa agir: o papel à esquerda, o que fazer à direita. */
const Agir = styled.dl`
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: 14px 12px;
  margin: 0;

  dt {
    display: flex;
    align-items: center;
    gap: 7px;
    height: 22px;
    font-size: 13px;
    font-weight: 600;
    color: var(--ink);
  }

  dd {
    margin: 0;
    min-width: 0;
    color: var(--ink);
  }

  dd ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 8px;
  }
`;

const PontoDoPapel = styled.span`
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $tom }) => tom($tom, "solid")};
`;

const Proximo = styled.div`
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  gap: 2px 8px;
  padding: 11px 14px 12px 12px;
  border-radius: 10px;
  background: var(--surface-2);

  > svg {
    grid-row: span 2;
    margin-top: 2px;
    color: var(--ink-2);
  }

  > b {
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-3);
  }

  > span {
    color: var(--ink);
    font-weight: 500;
    text-wrap: pretty;
  }
`;

const Abrir = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  justify-self: start;
  height: 30px;
  margin: -4px 0 0 -8px;
  padding: 0 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--ink-2);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--surface-2);
    color: var(--ink);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 0;
  }

  svg {
    transition: transform 160ms cubic-bezier(0.23, 1, 0.32, 1);
    transform: rotate(${({ $aberto }) => ($aberto ? "180deg" : "0deg")});
  }

  @media (prefers-reduced-motion: reduce) {
    svg {
      transition: none;
    }
  }
`;

const validos = (lista) => (lista || []).map((item) => String(item || "").trim()).filter(Boolean);

function Decisoes({ itens }) {
  const [abertas, setAbertas] = useState(false);
  const id = useId();
  if (!itens.length) return null;
  return (
    <Bloco aria-label="Decisões">
      <Abrir type="button" $aberto={abertas} aria-expanded={abertas} aria-controls={id} onClick={() => setAbertas((v) => !v)}>
        {abertas ? "Esconder as decisões" : `Ver decisões (${itens.length})`}
        <FiChevronDown size={14} aria-hidden="true" />
      </Abrir>
      {abertas ? (
        <Pontos id={id}>
          {itens.map((item, i) => <li key={i}>{item}</li>)}
        </Pontos>
      ) : null}
    </Bloco>
  );
}

/** O conteúdo da prévia (sem moldura): o mesmo no topo da conversa e no balão da lista. */
export function ConteudoDaPrevia({ previa }) {
  if (!previa) return null;
  const estado = estadoDaPrevia(previa);
  const grupos = quemPrecisaAgir(previa.falta);
  const pontos = validos(previa.topicos).slice(0, MAXIMO_DE_PONTOS);
  const decisoes = validos(previa.decisoes);
  const proximo = String(previa.proximo_passo || "").trim();
  return (
    <Leitura>
      <Topo>
        {estado ? <Estado $tom={estado.tom}>{estado.texto}</Estado> : null}
        {previa.situacao ? <Manchete>{previa.situacao}</Manchete> : null}
      </Topo>
      {grupos.length ? (
        <Bloco aria-label="Quem precisa agir">
          <h4>Quem precisa agir</h4>
          <Agir>
            {grupos.map((grupo) => (
              <React.Fragment key={grupo.quem}>
                <dt>
                  <PontoDoPapel $tom={grupo.tom} aria-hidden="true" />
                  {grupo.rotulo}
                </dt>
                <dd>
                  <ul>
                    {grupo.itens.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                </dd>
              </React.Fragment>
            ))}
          </Agir>
        </Bloco>
      ) : null}
      {pontos.length ? (
        <Bloco aria-label="O que aconteceu">
          <h4>O que aconteceu</h4>
          <Pontos>
            {pontos.map((item, i) => <li key={i}>{item}</li>)}
          </Pontos>
        </Bloco>
      ) : null}
      {proximo ? (
        <Proximo>
          <FiArrowRight size={15} aria-hidden="true" />
          <b>Próximo passo</b>
          <span>{proximo}</span>
        </Proximo>
      ) : null}
      <Decisoes itens={decisoes} />
    </Leitura>
  );
}

/* ---------- Rodapé e estados de carga ---------- */

const Nota = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: ${({ $tom }) => ($tom ? tom($tom, "text") : "var(--ink-3)")};
`;

const EsqueletoDaPrevia = styled.div`
  display: grid;
  gap: 10px;
  max-width: 64ch;

  > span:first-child {
    margin-bottom: 4px;
  }
`;

export function CarregandoPrevia() {
  return (
    <EsqueletoDaPrevia role="status" aria-label="Gerando a prévia">
      <Osso $altura={10} $largura="38%" />
      <Osso $altura={15} $largura="92%" />
      <Osso $altura={15} $largura="64%" />
      <Osso $altura={10} $largura="80%" style={{ marginTop: 12 }} />
      <Osso $altura={10} $largura="70%" />
    </EsqueletoDaPrevia>
  );
}

const Pedir = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
  margin-top: 10px;

  > span {
    flex: 1 1 200px;
    min-width: 0;
    font-size: 12px;
    line-height: 1.45;
    color: ${({ $esgotado }) => ($esgotado ? "var(--warning-text)" : "var(--ink-3)")};
    text-wrap: pretty;
  }
`;

/** O botão que gasta 1 pedido do dia, com o aviso de quantos sobram ao lado. */
function PedirAoGemini({ rotulo, gemini, aoPedir }) {
  const aviso = avisoDoPedido(gemini, "previa");
  return (
    <Pedir $esgotado={aviso.esgotado}>
      <BotaoCompacto type="button" onClick={aoPedir} disabled={aviso.esgotado} title={aviso.texto}>
        <FiRefreshCw size={14} aria-hidden="true" />
        {rotulo}
      </BotaoCompacto>
      <span>{aviso.texto}</span>
    </Pedir>
  );
}

/**
 * Linha de baixo: de onde veio a prévia e, se chegaram mensagens depois, o
 * "Atualizar prévia". `estado`: { carregando, erro } da última chamada; o
 * erro é o `detalhe` do servidor, como veio.
 */
export function RodapeDaPrevia({ previa, estado, gemini, aoAtualizar }) {
  if (estado.carregando) {
    return (
      <Nota>
        <MiniSpinner size={12} /> Atualizando com as mensagens novas…
      </Nota>
    );
  }
  if (estado.erro) {
    return (
      <Nota $tom="danger" role="alert">
        {estado.erro}
        <BotaoDeTexto type="button" onClick={aoAtualizar}>Tentar de novo</BotaoDeTexto>
      </Nota>
    );
  }
  const origem = textoDaPrevia(previa);
  const desatualizada = previa?.atualizada === false;
  return (
    <div>
      <Nota>
        {[origem, desatualizada ? "chegaram mensagens depois" : ""].filter(Boolean).join(" · ")}
      </Nota>
      {desatualizada ? <PedirAoGemini rotulo="Atualizar prévia" gemini={gemini} aoPedir={aoAtualizar} /> : null}
    </div>
  );
}

/** Sem prévia: esqueleto enquanto gera, erro com "Tentar de novo" ou o "Gerar agora" com o aviso da cota. */
export function PreviaAusente({ conversa, estado, gemini, aoAtualizar }) {
  if (estado.carregando) return <CarregandoPrevia />;
  if (estado.erro) {
    return (
      <Nota $tom="danger" role="alert">
        {estado.erro}
        <BotaoDeTexto type="button" onClick={aoAtualizar}>Tentar de novo</BotaoDeTexto>
      </Nota>
    );
  }
  if ((conversa?.mensagens_total ?? 1) <= 0) return <Nota>Ainda não há mensagens guardadas para resumir.</Nota>;
  return (
    <div>
      <Nota>Ainda sem prévia: ela sai na próxima rodada da IA.</Nota>
      <PedirAoGemini rotulo="Gerar agora" gemini={gemini} aoPedir={aoAtualizar} />
    </div>
  );
}

/* ---------- Faixa do topo da conversa ---------- */

const Caixa = styled.section`
  flex: 0 0 auto;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
`;

const Faixa = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 6px 10px 6px 6px;
`;

const Alternar = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 38px;
  padding: 0 8px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--ink);
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--surface-2);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }

  > svg:last-child {
    flex: 0 0 auto;
    margin-left: auto;
    color: var(--ink-3);
    transition: transform 160ms cubic-bezier(0.23, 1, 0.32, 1);
    transform: rotate(${({ $aberto }) => ($aberto ? "180deg" : "0deg")});
  }

  @media (prefers-reduced-motion: reduce) {
    > svg:last-child {
      transition: none;
    }
  }
`;

const Marca = styled.span`
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: var(--surface-2);
  color: var(--ink-2);
`;

const TextoDaFaixa = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13.5px;
  font-weight: ${({ $titulo }) => ($titulo ? 600 : 500)};
  color: ${({ $fraco }) => ($fraco ? "var(--ink-3)" : "var(--ink)")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* Quem espera, na faixa recolhida: ponto + rótulo (o rótulo some na conversa estreita). */
const EstadoDaFaixa = styled.span.attrs({ className: "cp-previa-estado" })`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-2);

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $tom }) => tom($tom, "solid")};
  }
`;

const Corpo = styled.div.attrs({ className: "cp-previa-corpo" })`
  max-height: min(52vh, 480px);
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 6px 20px 16px 50px;
`;

const RodapeDoCorpo = styled.div`
  margin-top: 16px;
`;

/**
 * `estado`: { carregando, erro } da chamada da prévia (feita pelo painel);
 * `gemini`: a cota do dia (para o aviso do botão).
 * Recolhida: a manchete numa linha. Aberta: o conteúdo inteiro.
 */
export default function CartaoDaPrevia({ conversa, estado, gemini, aoAtualizar }) {
  const [recolhida, setRecolhida] = useState(lerRecolhida);
  const idDoCorpo = useId();
  const previa = conversa.previa || null;
  const situacao = estadoDaPrevia(previa);

  function alternar() {
    setRecolhida((atual) => {
      gravarRecolhida(!atual);
      return !atual;
    });
  }

  let textoDaFaixa;
  if (!recolhida) textoDaFaixa = <TextoDaFaixa $titulo>Prévia da IA</TextoDaFaixa>;
  else if (previa?.situacao) textoDaFaixa = <TextoDaFaixa title={previa.situacao}>{previa.situacao}</TextoDaFaixa>;
  else if (estado.carregando) textoDaFaixa = <TextoDaFaixa $fraco>Gerando a prévia…</TextoDaFaixa>;
  else textoDaFaixa = <TextoDaFaixa $fraco>Prévia da IA</TextoDaFaixa>;

  const desatualizada = previa?.atualizada === false;
  const aviso = avisoDoPedido(gemini, "previa");
  return (
    <Caixa aria-label="Prévia da IA">
      <Faixa>
        <Alternar
          type="button"
          $aberto={!recolhida}
          aria-expanded={!recolhida}
          aria-controls={idDoCorpo}
          aria-label={recolhida ? "Abrir a prévia da IA" : "Recolher a prévia da IA"}
          onClick={alternar}
        >
          <Marca><IconeDaIA tamanho={15} /></Marca>
          {textoDaFaixa}
          {recolhida && situacao ? (
            <EstadoDaFaixa $tom={situacao.tom} title={situacao.texto}>
              <span className="rotulo">{situacao.curto}</span>
            </EstadoDaFaixa>
          ) : null}
          <FiChevronDown size={16} aria-hidden="true" />
        </Alternar>
        {recolhida && (desatualizada || (estado.carregando && previa)) ? (
          <BotaoCompacto
            type="button"
            onClick={aoAtualizar}
            disabled={estado.carregando || aviso.esgotado}
            aria-label="Atualizar a prévia com as mensagens novas"
            title={`Chegaram mensagens depois desta prévia. ${aviso.texto}`}
          >
            {estado.carregando ? <MiniSpinner size={14} /> : <FiRefreshCw size={14} aria-hidden="true" />}
            <span className="rotulo">Atualizar</span>
          </BotaoCompacto>
        ) : null}
      </Faixa>
      {recolhida ? null : (
        <Corpo id={idDoCorpo}>
          {previa ? (
            <>
              <ConteudoDaPrevia previa={previa} />
              <RodapeDoCorpo>
                <RodapeDaPrevia previa={previa} estado={estado} gemini={gemini} aoAtualizar={aoAtualizar} />
              </RodapeDoCorpo>
            </>
          ) : (
            <PreviaAusente conversa={conversa} estado={estado} gemini={gemini} aoAtualizar={aoAtualizar} />
          )}
        </Corpo>
      )}
    </Caixa>
  );
}
